import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Assignment as AssignmentIcon } from '@material-ui/icons';
import { StateNodeType } from '@sierralabs/workflow';
import {
  TaskStatusLabel as TaskStatusLabelV2,
  useTaskRedirect as useTaskV2Redirect
} from '@sierralabs/workflow-ui';

import AppSnackbar from '../../common/AppSnackbar';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import { FetchError } from '../../common/api/api';
import { formatDate } from '../../common/formatter';
import { useUserContext } from '../../user/User.context';
import { DashboardItem } from '../dashboard/Dashboard.interface';
import BaseDashboardCard, {
  DashboardCardOrder
} from '../dashboard/components/BaseDashboardCard';
import { HazardTaskTypeEntities } from '../hazards/Hazard.interface';
import { ProductTaskTypeEntities } from '../products/Product.interface';
import { getTaskPath, useFindTaskList } from '../tasks/Task.hooks';
import { TaskListItem } from '../tasks/Task.interface';

// types to use task.entityId instead of task.key
// task.entityId contains {prefix}-{actual id of entity}
// task.key contains {prefix}-{increment id}
const entityIdType: string[] = [
  ...ProductTaskTypeEntities,
  ...HazardTaskTypeEntities
];

const orderMap: { [key in DashboardCardOrder]: string } = {
  title: 'task.modified',
  status: 'task.status'
};

const AdminToDoCard: React.FC = () => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [order, setOrder] = useState(orderMap.title);
  const [direction, setDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [error, setError] = useState<FetchError>();
  // disable clicking items while the selected item is loading for redirect
  const [loading, setLoading] = useState(false);
  const { user } = useUserContext();
  const {
    taskList,
    isLoading: isFinding,
    error: findError,
    findTasks
  } = useFindTaskList();
  // redirect task v2
  const { getRedirectUrl: getRedirectUrlV2 } = useTaskV2Redirect();
  const isLoading = isFinding || loading;
  const assigneeId = user?.id;

  const items = useMemo(() => {
    return tasks.map((task): DashboardItem<TaskListItem> => {
      // use entityId for v1 Tasks when available
      // prioritize parent task since the task can be a subTask
      // NOTE: `entityId` will be deprecated and can be removed once `SLQMS-921` is merged
      const taskTypeV1 =
        task.apiLevel === 1
          ? task.parent?.taskType || task.taskType
          : undefined;
      const taskKey =
        (task.apiLevel === 1 &&
        taskTypeV1?.targetEntity &&
        entityIdType.includes(taskTypeV1.targetEntity)
          ? task.entityId
          : undefined) ||
        task.parent?.key ||
        task.key;

      const taskType = task.parent?.taskType || task.taskType;
      const primary = (taskKey ? `${taskKey} / ` : '') + task.name;
      const tooltip = primary + (taskType?.label ? ` / ${taskType.label}` : '');

      // task v2
      if (task.apiLevel === 2) {
        return {
          id: `task-v2-${task.id}`,
          meta: task,
          disabled: loading,
          columns: [
            {
              tooltip,
              primary,
              secondary: formatDate(task.assigned || task.modified, true)
            },
            { render: () => <TaskStatusLabelV2 task={task} /> }
          ],
          onClick: async () => {
            setLoading(true);
            const path = await getRedirectUrlV2(task);
            setLoading(false);

            if (path) {
              history.push(path);
            } else {
              setError({
                statusCode: 404,
                error: 'Not Found',
                message: 'Unable to redirect to Task item.'
              });
            }
          }
        };
      }

      // task v1
      return {
        id: `task-v1-${task.id || 0}`,
        meta: task,
        disabled: loading,
        columns: [
          { tooltip, primary, secondary: formatDate(task.modified, true) },
          { render: () => <TaskStatusLabel task={task} /> }
        ],
        onClick: async () => {
          setLoading(true);
          const targetTask = task.parent || task;
          const result = await getTaskPath({
            taskId: targetTask.id || 0,
            targetEntity: targetTask.taskType?.targetEntity
          });
          setLoading(false);

          if (!result) {
            // do nothing
          } else if (result.path) {
            history.push(result.path);
          } else if (result.error) {
            setError(result.error);
          }
        }
      };
    });
  }, [history, loading, tasks, getRedirectUrlV2]);

  useEffect(() => {
    // sort items manually
    findTasks({
      page,
      limit,
      order,
      direction,
      assigneeIds: assigneeId ? [assigneeId] : [],
      excludeTypes: [StateNodeType.Final]
    });
  }, [assigneeId, direction, limit, order, page, findTasks]);

  useEffect(() => {
    taskList && setTasks(taskList.data);
  }, [taskList]);

  useEffect(() => {
    setError(findError);
  }, [findError]);

  return (
    <>
      <BaseDashboardCard
        pagination
        useGraph={false}
        items={items}
        total={taskList?.total}
        isLoading={isLoading}
        icon={<AssignmentIcon />}
        title='To-do List'
        emptyState='No To-do Items'
        page={page}
        limit={limit}
        orderDirection={direction}
        setPage={setPage}
        setLimit={setLimit}
        setOrderDirection={setDirection}
        isActive={(o: DashboardCardOrder) => orderMap[o] === order}
        setOrder={(order: DashboardCardOrder) => setOrder(orderMap[order])}
      />
      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message || 'An error occurred.'}
      </AppSnackbar>
    </>
  );
};

export default AdminToDoCard;
